import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const Terms = () => {
  return (
    <div className="min-h-screen py-8 px-4" style={{
      background: `radial-gradient(900px 600px at 10% 10%, #132552 0%, #0B1736 45%, #060f24 100%)`
    }}>
      <main className="max-w-5xl mx-auto">
        <div className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/">
              <ArrowLeft className="mr-2 w-4 h-4" /> Back to Home
            </Link>
          </Button>
          
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <div>
              <h1 className="font-orbitron font-bold text-4xl md:text-5xl mb-2">
                Terms & <span className="text-primary">Conditions</span>
              </h1>
              <p className="text-muted-foreground">
                Business-to-Business General Terms, Conditions & Service Agreement
              </p>
            </div>
            <Button asChild>
              <a href="/NeilAI_Terms_and_Conditions.pdf" download>
                <Download className="mr-2 w-4 h-4" /> Download PDF
              </a>
            </Button>
          </div>
        </div>

        <Card className="glass-effect border-[hsl(var(--line))] mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">1. Introduction & Scope</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-muted-foreground">
            <p>
              These Terms and Conditions apply to all business-to-business (B2B) transactions with Neil Ai Global Solutions, 
              including equipment purchases, leases, installations, maintenance, and Robot-as-a-Service (RaaS) agreements. 
              By signing, the Customer agrees to abide by these terms.
            </p>
          </CardContent>
        </Card>

        <Card className="glass-effect border-[hsl(var(--line))] mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">2. Returns, Restocking & Shipping Policy</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-muted-foreground">
            <p>
              Returns are accepted within 30 days for unopened products, subject to a 50% restocking fee. Opening the box 
              waives refund eligibility. All returns require an RMA form. Customers are responsible for shipping to and from 
              Neil Ai Global Solutions for warranty repairs. International customers must mark shipments as 'temporary import 
              for repair' to avoid taxes.
            </p>
          </CardContent>
        </Card>

        <Card className="glass-effect border-[hsl(var(--line))] mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">3. Service Cancellation & Early Termination</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-muted-foreground">
            <p>
              Service cancellations are allowed up to 1 week before the scheduled service date. Early termination of a lease 
              or payment plan requires return of equipment in working condition and payment of 50% of the remaining contract 
              balance. For RaaS agreements, early termination requires a 3-month penalty and payment of the first year 
              commitment if canceled within year one.
            </p>
          </CardContent>
        </Card>

        <Card className="glass-effect border-[hsl(var(--line))] mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">4. Ownership, Usage & Restrictions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-muted-foreground">
            <p>
              All equipment remains property of Neil Ai Global Solutions until fully paid. Customers may not modify, resell, 
              or reverse-engineer any hardware or software. Usage rights are non-transferable and granted solely for the 
              duration of the contract.
            </p>
          </CardContent>
        </Card>

        <Card className="glass-effect border-[hsl(var(--line))] mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">5. Gold & Platinum Service Contracts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-muted-foreground">
            <div>
              <h3 className="font-semibold text-foreground mb-2">Gold Service Plan (Included Free):</h3>
              <p>
                Covers normal wear and tear, manufacturer defects, software updates, unlimited phone & email support during 
                business hours, and shipping for warranty repairs.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-2">Platinum Service Plan ($600 per unit):</h3>
              <p>
                Includes all Gold features plus 24/7 priority support, swap program with replacement within 2 business days, 
                yearly preventive maintenance, refresher training, and priority response times (&lt;2 hours initial, 4 hours 
                workaround, 8 hours full resolution). All customers may enroll for $600 per unit.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-effect border-[hsl(var(--line))] mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">6. Training</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-muted-foreground">
            <p>
              Training must be scheduled at least 2 weeks in advance. Customers must ensure site readiness and provide 
              appropriate facilities. Travel and lodging expenses are billed separately. On-site training typically runs 
              1-2 full business days.
            </p>
          </CardContent>
        </Card>

        <Card className="glass-effect border-[hsl(var(--line))] mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">7. Financing & Payments</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-muted-foreground">
            <p>
              Financing is provided through third-party partners such as Ascentium Capital. Neil Ai Global Solutions is not 
              responsible for approval or terms. Payments are due as stated in the agreement. Late payments incur a 1.5% 
              monthly fee. Prices may be adjusted upon renewal with 30 days' notice. Neil Ai operates as a cashless business; 
              cash deposits are non-refundable.
            </p>
          </CardContent>
        </Card>

        <Card className="glass-effect border-[hsl(var(--line))] mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">8. Maintenance & Support Levels</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-muted-foreground">
            <ul className="space-y-2">
              <li><strong className="text-foreground">Silver:</strong> Basic warranty and updates.</li>
              <li><strong className="text-foreground">Gold:</strong> Included free; full coverage of normal issues, shipping, and standard support.</li>
              <li><strong className="text-foreground">Platinum:</strong> Premium response times, preventive maintenance, and 24/7 support. See Section 5 for details.</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="glass-effect border-[hsl(var(--line))] mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">9. Customer Responsibilities</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-muted-foreground">
            <p>
              Customers must use equipment properly, maintain a suitable operating environment (power, connectivity, space), 
              and comply with applicable data privacy regulations. Customers are responsible for protecting sensitive data 
              and maintaining system security.
            </p>
          </CardContent>
        </Card>

        <Card className="glass-effect border-[hsl(var(--line))] mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">10. Liability & Indemnification</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-muted-foreground">
            <p>
              Neil Ai Global Solutions is not liable for indirect, incidental, or consequential damages. The Customer agrees 
              to indemnify and hold Neil Ai harmless against all claims, damages, or liabilities arising from misuse or 
              third-party claims related to Customer operations.
            </p>
          </CardContent>
        </Card>

        <Card className="glass-effect border-[hsl(var(--line))] mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">11. Termination & Return of Equipment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-muted-foreground">
            <p>
              Neil Ai may terminate agreements if the Customer breaches terms and fails to remedy within 10 days. Upon 
              termination, equipment must be returned in good working condition. Failure to return may result in additional 
              fees and legal action.
            </p>
          </CardContent>
        </Card>

        <Card className="glass-effect border-[hsl(var(--line))] mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">12. Confidentiality & Data Usage</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-muted-foreground">
            <p>
              Both parties agree to maintain confidentiality of proprietary information. Neil Ai may collect anonymized usage 
              data to improve products and services but will not share sensitive data without consent.
            </p>
          </CardContent>
        </Card>

        <Card className="glass-effect border-[hsl(var(--line))] mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">13. Legal Terms (B2B)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-muted-foreground">
            <p>
              These terms constitute the entire agreement between Neil Ai Global Solutions and the Customer. Any modifications 
              must be in writing and signed by both parties. This agreement is governed by the laws of North Carolina.
            </p>
          </CardContent>
        </Card>

        <Card className="glass-effect border-[hsl(var(--line))] mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">14. Force Majeure</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-muted-foreground">
            <p>
              Neither party is liable for failure to perform obligations due to circumstances beyond their control, including 
              natural disasters, war, strikes, or government actions.
            </p>
          </CardContent>
        </Card>

        <div className="text-center py-8">
          <p className="text-muted-foreground mb-4">
            Have questions about our terms? Contact us today.
          </p>
          <Button asChild size="lg">
            <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
              Contact Sales
            </a>
          </Button>
        </div>
      </main>
    </div>
  );
};

export default Terms;
